/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: ImportDevicesModal.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Provisioning Devices module.
----------------------------------------------------------------*/

import React, { useState } from 'react';
import { X, Upload, FileSpreadsheet } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import * as api from './api';

interface ImportDevicesModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export default function ImportDevicesModal({ isOpen, onClose }: ImportDevicesModalProps) {
    const { t } = useI18n();
    const { showSuccess, showError } = useNotifications();
    const [file, setFile] = useState<File | null>(null);
    const [isUploading, setIsUploading] = useState(false);

    if (!isOpen) return null;

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setFile(e.target.files[0]);
        }
    };

    const handleImport = async () => {
        if (!file) return;

        setIsUploading(true);
        try {
            const content = await file.text();
            const result = await api.importDevices(content);
            if (result.success) {
                if (result.errors && result.errors.length > 0) {
                    showError(t('provisioningDevices.importWarnings', { count: result.count, errors: result.errors.join(', ') }));
                } else {
                    showSuccess(t('provisioningDevices.importSuccess', { count: result.count }));
                }
                onClose();
            } else {
                showError(t('provisioningDevices.importError', { errors: result.errors.join(', ') }));
            }
        } catch (error) {
            console.error(error);
            showError(t('common.error'));
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-background rounded-lg shadow-xl w-full max-w-md p-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">{t('provisioningDevices.import', 'Import Devices')}</h3>
                    <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="space-y-4">
                    <div
                        className="border-2 border-dashed border-border rounded-lg p-8 text-center transition-colors hover:bg-muted/10"
                        onDragOver={e => e.preventDefault()}
                        onDrop={e => {
                            e.preventDefault();
                            if (e.dataTransfer.files[0]) setFile(e.dataTransfer.files[0]);
                        }}
                    >
                        <FileSpreadsheet className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                        <p className="text-sm text-muted-foreground mb-4">
                            Drag and drop your CSV file here, or click to browse
                        </p>
                        <input
                            type="file"
                            accept=".csv"
                            onChange={e => e.target.files?.[0] && setFile(e.target.files[0])}
                            className="hidden"
                            id="csv-upload"
                        />
                        <label htmlFor="csv-upload" className="inline-block px-4 py-2 bg-secondary rounded-md cursor-pointer hover:bg-secondary/90 transition-colors">
                            {t('common.browse', 'Browse')}
                        </label>
                        {file && (
                            <div className="mt-4 text-sm font-medium text-foreground">
                                {file.name}
                            </div>
                        )}
                    </div>
                    <div className="text-xs text-muted-foreground">
                        <p>Expected CSV headers: mac, model, vendor, template, description</p>
                    </div>

                    <div className="flex justify-end gap-2">
                        <button onClick={onClose} className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground">
                            {t('common.cancel', 'Cancel')}
                        </button>
                        <button
                            onClick={handleImport}
                            disabled={!file || isUploading}
                            className="px-4 py-2 bg-primary text-primary-foreground rounded-md disabled:opacity-50"
                        >
                            {isUploading ? t('common.importing', 'Importing...') : t('common.import', 'Import')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
